'use client';
import * as React from 'react';
import { useTranslations } from '@mentimeter/i18n';
import { format } from 'date-fns/format';
import { blue500, gray1300, whiteAlpha400 } from '@mentimeter/ragnar-colors';
import { getContentfulAssetImageProps } from 'src/cms/utils/utils';
import type { ICategory, Asset } from 'types/generated/contentful';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Image } from '@mentimeter/ragnar-ui/image';
import { Link } from '@mentimeter/ragnar-ui/link';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Wide } from '../layout';
import { H1 } from '../typography';

interface HeaderProps {
  category: ICategory;
  title: string;
  date: string;
  time: string | undefined;
  image: Asset;
}

export const Header = ({ category, title, date, time, image }: HeaderProps) => {
  const t = useTranslations('common');
  const formattedDate = format(new Date(date), 'MMMM dd, yyyy');

  return (
    <Wide
      flexDirection={['column', null, 'row']}
      alignItems="stretch"
      pb={['space2', null, 'space0']}
      mb={['space4', null, 'space16']}
    >
      <Box
        px={[null, null, 'space16']}
        flex={['1 0 auto', null, '1 0 523px']}
        bg={[null, null, gray1300]}
        alignItems="stretch"
      >
        <Box
          flex="1 1 auto"
          alignItems={[null, null, 'center']}
          justifyContent="center"
        >
          <Box flexDirection="row" mb={['space3', null, 'space6']}>
            <Link
              fontSize={['87.5', null, '100']}
              color={blue500}
              underline={false}
              href="/blog"
              aria-label="Go to blog"
            >
              {t('page_links.blog')}
            </Link>
            <Text
              fontSize={['87.5', null, null, '100']}
              color={blue500}
              mx="space2"
            >
              /
            </Text>
            <Link
              fontSize={['87.5', null, '100']}
              color={blue500}
              underline={false}
              href={`/blog/${category.fields?.slug || ''}`}
              aria-label="Go to category page"
            >
              {category.fields?.title}
            </Link>
          </Box>
          <H1
            color={[null, null, 'bg']}
            textAlign={[null, null, 'center']}
            fontSize={['200', null, 7]} // we don't have 38px in kosmos
            fontWeight="regular"
            lineHeight="snug"
            mb="space0"
          >
            {title}
          </H1>
        </Box>
        <Box
          alignItems={[null, null, 'center']}
          borderTopWidth="1px"
          borderStyle="solid"
          borderColor={whiteAlpha400}
          py="space2"
        >
          <Box
            flexDirection="row"
            pt={['space1', null, 'space4']}
            pb={['space4', null, 'space4']}
          >
            <Text
              fontSize="87.5"
              lineHeight="relaxed"
              color={[null, null, 'bg']}
            >
              {formattedDate}
            </Text>
            {time && (
              <>
                <Text
                  fontSize="87.5"
                  lineHeight="relaxed"
                  color={[null, null, 'bg']}
                  mx="space2"
                >
                  /
                </Text>
                <Text
                  fontSize="87.5"
                  lineHeight="relaxed"
                  color={[null, null, 'bg']}
                >
                  {time} min read
                </Text>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        flex={['0 1 auto', null, '0 1 677px']}
        overflow="hidden"
        // @ts-expect-error Not in kosmos
        borderBottomRightRadius={['60px', null, '80px']}
      >
        <Image
          {...getContentfulAssetImageProps(image)}
          alt={image.fields.title || ''}
          height={['initial', '384px']}
          width="100%"
          extend={() => ({
            objectFit: 'cover',
          })}
        />
      </Box>
    </Wide>
  );
};
